import { todayIso } from '../dates'
import { addDays, amsterdamDay, shortDay, timeOfDay } from '../record/format'
import type { ActivityKind, ChangeValues, Decision, FollowUp, Mention, VoiceResult } from './types'

export type ProposalKind = 'activity' | 'task' | 'change' | 'new'

export interface ProposalRef {
  ref: string
  kind: ProposalKind
  blocked: boolean
  /** A change that no agreement is linked to yet: it can be edited (to pick one) but not ticked. */
  unlinked: boolean
  /** A change that a new agreement replaced is shown as that new agreement only. */
  superseded: boolean
  acceptDefault: boolean
}

/** Every proposal of a result, in screen order. */
export function proposalRefs(result: VoiceResult, edits: Record<string, Record<string, unknown>> = {}): ProposalRef[] {
  return [
    ...result.activities.map((item) => ({
      ref: item.ref,
      kind: 'activity' as const,
      blocked: item.blocked_by.length > 0,
      unlinked: false,
      superseded: false,
      acceptDefault: item.accept_default,
    })),
    ...result.tasks.map((item) => ({
      ref: item.ref,
      kind: 'task' as const,
      blocked: item.blocked_by.length > 0,
      unlinked: false,
      superseded: false,
      acceptDefault: item.accept_default,
    })),
    ...result.agreement_changes.map((item) => ({
      ref: item.ref,
      kind: 'change' as const,
      blocked: item.blocked_by.length > 0,
      // Without one agreement the server cannot write the change: pick one in the sheet first.
      unlinked: !((edits[item.ref]?.agreement_id as string | undefined) ?? item.agreement_id),
      superseded: item.superseded_by != null,
      acceptDefault: item.accept_default,
    })),
    ...result.new_agreements.map((item) => ({
      ref: item.ref,
      kind: 'new' as const,
      blocked: item.blocked_by.length > 0,
      unlinked: false,
      superseded: false,
      acceptDefault: item.accept_default,
    })),
  ]
}

/** What the checkbox of each proposal shows: the user's tick, else the server's default; blocked ones never. */
export function effectiveChecks(
  result: VoiceResult,
  touched: Record<string, boolean>,
  edits: Record<string, Record<string, unknown>> = {},
): Record<string, boolean> {
  const checks: Record<string, boolean> = {}
  for (const item of proposalRefs(result, edits)) {
    checks[item.ref] = !item.blocked && !item.unlinked && !item.superseded && (touched[item.ref] ?? item.acceptDefault)
  }
  return checks
}

export function checkedCount(checks: Record<string, boolean>): number {
  return Object.values(checks).filter(Boolean).length
}

/** The first mention that a proposal is waiting on (the user has to choose under "Wie"). */
export function waitingMention(result: VoiceResult): Mention | null {
  const waiting = new Set<string>()
  for (const group of [result.activities, result.tasks, result.agreement_changes, result.new_agreements]) {
    for (const item of group) for (const ref of item.blocked_by) waiting.add(ref)
  }
  return result.mentions.find((mention) => waiting.has(mention.ref)) ?? null
}

/** Whether a memo has no proposal at all ("Hier kon Sparky niets mee"). */
export function isEmptyResult(result: VoiceResult): boolean {
  return (
    result.activities.length === 0 &&
    result.tasks.length === 0 &&
    result.agreement_changes.length === 0 &&
    result.new_agreements.length === 0
  )
}

export function decisionsFor(
  result: VoiceResult,
  checks: Record<string, boolean>,
  edits: Record<string, Record<string, unknown>>,
): Decision[] {
  return proposalRefs(result).map(({ ref }) => {
    const values = edits[ref]
    return { ref, accept: checks[ref] === true, ...(values ? { values } : {}) }
  })
}

export const ACTIVITY_LABEL: Record<ActivityKind, string> = {
  call: 'Gesprek',
  meeting: 'Meeting',
  email: 'E-mail',
  note: 'Notitie',
}

export const STATUS_LABEL = {
  pauzeren: 'Pauzeren',
  hervatten: 'Hervatten',
  beeindigen: 'Beëindigen',
} as const

const UNIT_LABEL = { week: 'weken', maand: 'maanden', jaar: 'jaar' } as const
const UNIT_ONE = { week: 'week', maand: 'maand', jaar: 'jaar' } as const

/** "elke 3 maanden", "elke maand", "minstens elke 2 weken contact". */
export function followUpText(follow: FollowUp | null | undefined): string | null {
  if (!follow?.schedule) return null
  const { schedule, interval_count: count, interval_unit: unit } = follow
  if (schedule === 'geen') return 'geen opvolging'
  if (schedule === 'eenmalig') return 'eenmalig'
  if (!unit) return null
  const every = count && count > 1 ? `elke ${count} ${UNIT_LABEL[unit]}` : `elke ${UNIT_ONE[unit]}`
  return schedule === 'contactritme' ? `minstens ${every} contact` : every
}

/** The fields a change touches, as Dutch lines ("Opvolging → elke 3 maanden"). */
export function changeLines(change: ChangeValues, formatDay: (iso: string) => string): string[] {
  const lines: string[] = []
  const follow = followUpText(change.follow_up)
  if (follow) lines.push(`Opvolging → ${follow}`)
  if (change.status) lines.push(STATUS_LABEL[change.status])
  if (change.ends_on) lines.push(`Einde → ${formatDay(change.ends_on)}`)
  if (change.postpone_until) lines.push(`Taak uitstellen tot ${formatDay(change.postpone_until)}`)
  if (change.title) lines.push(`Titel → ${change.title}`)
  if (change.owner_user_id) lines.push('Andere eigenaar')
  if (change.addendum) lines.push(`Aanvulling: ${change.addendum}`)
  return lines
}

/** The outcome of one mention, in text (never colour alone, IA-20). */
export function mentionOutcome(mention: Mention): string {
  const chosen = mention.chosen
  const named = (name: string | null | undefined, organisation: string | null | undefined) =>
    organisation ? `${name ?? mention.name} · ${organisation}` : (name ?? mention.name)
  if (chosen) {
    if (chosen.type === 'geen') return `Niet gekoppeld: '${mention.name}'`
    if (chosen.type === 'nieuw_contact') return `+ Nieuw contact: ${named(chosen.name, chosen.organisation_name)}`
    const label = named(chosen.name, chosen.organisation_name)
    if (mention.certainty === 'controleer') return `Controleer: ${label}`
    if (mention.certainty === 'zeker') return `✓ ${label}`
    return `✓ ${label} (jouw keuze)`
  }
  if (mention.certainty === 'kiezen') return `! '${mention.name}': kies wie je bedoelt`
  if (mention.certainty === 'niet_gevonden') return `? Niet gevonden: '${mention.name}'`
  return `✓ ${mention.name}`
}

/** "morgen 14:32": until when an undo is possible. */
export function undoUntilText(moment: string): string {
  const day = amsterdamDay(moment)
  const today = todayIso()
  const label = day === today ? 'vandaag' : day === addDays(today, 1) ? 'morgen' : shortDay(day)
  return `${label} ${timeOfDay(moment)}`
}
